import {I18nKeys} from '@multipass-i18n';
import {Button, Checkbox, ChoiceCard, ChoiceCardCaption, ChoiceCardGroup, DecisionGate} from '@multipass-ui-components';
import {ScriptPreferences} from '@multipass-user-service-dto';
import {usePublishEvent} from 'esp-js-react';
import {ArrowUpRight, FileText, Info, Languages, User} from 'lucide-react';
import * as React from 'react';

import {ScriptPreferenceChosenEvent, SESSION_STORE_ID, SessionEvents, SessionStoreModel, useT, useTReason} from '../../logic';
import {iconFor, titleKeyFor} from '../legal/legalDocumentDisplay';

const SignedInAs: React.FunctionComponent<{email: string}> = ({email}: {email: string}) => {
    const t = useT();
    return <><User size={14}/> {t(I18nKeys.common.signedInAs)} {email}</>;
};

const ScriptSelectionChild: React.FunctionComponent<{session: SessionStoreModel}> = ({session}: {session: SessionStoreModel}) => {
    const publishEvent = usePublishEvent();
    const t = useT();
    const tReason = useTReason();
    const [selected, setSelected] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const scriptError = session.scriptError;
    React.useEffect(() => {
        if (scriptError !== null) {
            setSubmitting(false);
        }
    }, [scriptError]);

    const onContinue = () => {
        if (selected === null || submitting) {
            return;
        }
        setSubmitting(true);
        publishEvent(SESSION_STORE_ID, SessionEvents.scriptPreferenceChosen, {scriptPreference: selected} satisfies ScriptPreferenceChosenEvent);
    };

    return (
        <DecisionGate
            card
            badge={<Languages size={26}/>}
            title={t(I18nKeys.decisionGate.scriptTitle)}
            sub={t(I18nKeys.decisionGate.scriptSub)}
            signedInAs={session.email !== null ? <SignedInAs email={session.email}/> : undefined}
            note={<><Info size={14}/> {t(I18nKeys.decisionGate.changeLaterNote)}</>}
            actions={
                <>
                    {scriptError !== null && (
                        <p role="alert" className="m-0 text-center text-caption text-error">{tReason(session.scriptErrorCode, scriptError)}</p>
                    )}
                    <Button type="button" size="lg" block disabled={selected === null || submitting} onClick={onContinue}>
                        {t(I18nKeys.decisionGate.continueCta)}
                    </Button>
                </>
            }
        >
            <ChoiceCardGroup>
                <ChoiceCard
                    glyph="學"
                    name={t(I18nKeys.decisionGate.traditionalName)}
                    meta={t(I18nKeys.decisionGate.traditionalMeta)}
                    selected={selected === ScriptPreferences.Traditional}
                    onSelect={() => setSelected(ScriptPreferences.Traditional)}
                />
                <ChoiceCard
                    glyph="学"
                    name={t(I18nKeys.decisionGate.simplifiedName)}
                    meta={t(I18nKeys.decisionGate.simplifiedMeta)}
                    selected={selected === ScriptPreferences.Simplified}
                    onSelect={() => setSelected(ScriptPreferences.Simplified)}
                />
            </ChoiceCardGroup>
            <ChoiceCardCaption>{t(I18nKeys.decisionGate.choiceCaption)}</ChoiceCardCaption>
        </DecisionGate>
    );
};

const UpdatedTermsChild: React.FunctionComponent<{session: SessionStoreModel}> = ({session}: {session: SessionStoreModel}) => {
    const publishEvent = usePublishEvent();
    const t = useT();
    const [mode, setMode] = React.useState<'review' | 'declineConfirm'>('review');
    const [agreed, setAgreed] = React.useState(false);

    const onAgree = () => {
        if (!agreed) {
            return;
        }
        publishEvent(SESSION_STORE_ID, SessionEvents.acceptTermsRequested, {});
    };
    const onSignOutForNow = () => {
        publishEvent(SESSION_STORE_ID, SessionEvents.loginRestartRequested, {});
    };

    if (mode === 'declineConfirm') {
        return (
            <DecisionGate
                card
                badge={<FileText size={26}/>}
                title={t(I18nKeys.reaccept.declineTitle)}
                sub={t(I18nKeys.reaccept.declineBody, {appName: t(I18nKeys.common.appName)})}
                actions={
                    <>
                        <Button type="button" size="lg" block onClick={() => setMode('review')}>{t(I18nKeys.reaccept.backToReview)}</Button>
                        <Button type="button" variant="secondary" size="lg" block onClick={onSignOutForNow}>{t(I18nKeys.reaccept.signOutForNow)}</Button>
                    </>
                }
            >
                <></>
            </DecisionGate>
        );
    }

    return (
        <DecisionGate
            card
            badge={<FileText size={26}/>}
            title={t(I18nKeys.reaccept.title)}
            sub={t(I18nKeys.reaccept.subtitle)}
            signedInAs={session.pendingEmail !== null ? <SignedInAs email={session.pendingEmail}/> : undefined}
            actions={<Button type="button" size="lg" block disabled={!agreed} onClick={onAgree}>{t(I18nKeys.reaccept.agree)}</Button>}
            escape={<button type="button" onClick={() => setMode('declineConfirm')}>{t(I18nKeys.reaccept.decline)}</button>}
        >
            <div className="flex flex-col gap-3">
                {session.outstandingDocuments.map(document => {
                    const Icon = iconFor(document.type);
                    return (
                        <a
                            key={document.id}
                            href={`/legal#${document.type}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center gap-4 rounded-lg border border-border-subtle bg-surface p-4 text-left no-underline transition-shadow duration-fast ease-standard hover:border-border hover:shadow-sm"
                        >
                            <span className="grid h-[42px] w-[42px] flex-none place-items-center rounded-md bg-brand/15 text-brand-emphasis">
                                <Icon size={20}/>
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block font-display font-semibold text-text-primary">{t(titleKeyFor(document.type))}</span>
                                <span className="mt-0.5 block text-caption text-text-muted">{t(I18nKeys.common.version, {version: document.version})}</span>
                            </span>
                            <span className="inline-flex flex-none items-center gap-1 text-label font-bold text-link">
                                {t(I18nKeys.reaccept.read)} <ArrowUpRight size={16}/>
                            </span>
                        </a>
                    );
                })}
            </div>
            <Checkbox boxed checked={agreed} onChange={setAgreed}>
                {t(I18nKeys.reaccept.consent)}
            </Checkbox>
        </DecisionGate>
    );
};

export const DecisionGateView: React.FunctionComponent<{session: SessionStoreModel}> = ({session}: {session: SessionStoreModel}) => {
    const child = session.pendingGateChild;
    if (child === null) {
        return null;
    }
    return (
        <div className="mx-auto w-full max-w-[560px] px-6 py-14">
            {child === 'scriptSelection' ? <ScriptSelectionChild session={session}/> : <UpdatedTermsChild session={session}/>}
        </div>
    );
};
